/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import {
  FoxitModule,
  FragmentComponentOptions_callback,
  getAddonPath,
  PDFUIOptions,
  UIFragmentOptions,
} from 'webcommon/lazy-load-commonjs/foxit';
import { FoxitLicenseDto } from 'webcommon/shared';
import { FoxitPdfRendererBase } from './FoxitPdfRendererBase';

@Injectable()
export class FoxitViewRenderer extends FoxitPdfRendererBase {
  override readonly pdfUI = this.createPDFUI();

  protected override getNewTemplate(): PDFUIOptions['template'] {
    return `
<webpdf>
  <toolbar name="toolbar" class="fv__ui-toolbar-scrollable">
    <tabs name="toolbar-tabs">
      <tab title="toolbar.tabs.home.title" name="home-tab">
        <group-list name="home-toolbar-group-list">
          <group name="home-tab-group-hand" retain-count="4">
            <hand-button></hand-button>
            <xbutton
              @controller="states:SelectTextImageController"
              name="select-text-image"
              @tooltip tooltip-title="toolbar.tooltip.selectTextImage.title"
              icon-class="fv__icon-toolbar-select-text-image"
            ></xbutton>
            <download-file-button></download-file-button>
            <print:print-button></print:print-button>
          </group>
          <group name="comment-tab-group-stamp" retain-count="2">
            <single-page-button></single-page-button>
            <continuous-page-button></continuous-page-button>
          </group>
          <group name="home-tab-group-nav" retain-count="2">
            <goto-page-input></goto-page-input>
          </group>
          <group name="home-tab-group-zoom">
            <zoom-out-button></zoom-out-button>
            <editable-zoom-dropdown></editable-zoom-dropdown>
            <zoom-in-button></zoom-in-button>
          </group>
        </group-list>
      </tab>
    </tabs>
  </toolbar>
  <div class="fv__ui-body">
    <sidebar name="sidebar">
      <thumbnail-sidebar-panel></thumbnail-sidebar-panel>
    </sidebar>
    <viewer></viewer>
  </div>
  <template name="template-container">
    <text-sel:text-selection-tooltip></text-sel:text-selection-tooltip>
  </template>
</webpdf>
    `;
  }

  onClickDownloadFile: FragmentComponentOptions_callback<PointerEvent> = (_e) => {
    // noop by default;
    // we could also make this default to the actual foxit default behavior if we want to
  };

  onClickPrintFile: FragmentComponentOptions_callback<PointerEvent> = (_e) => {
    // noop by default;
    // if you want the default behavior,
    // you need <print:print-dialog></print:print-dialog> in the <template> tag
  };

  protected override getOptions(
    foxitModule: FoxitModule,
    foxitSetup: FoxitLicenseDto,
    element: HTMLElement,
  ): PDFUIOptions {
    const defaultOptions = super.getOptions(foxitModule, foxitSetup, element);
    defaultOptions.fragments = [
      {
        config: {
          callback: (...args) => this.onClickDownloadFile(...args),
        },
        target: 'download-file-button',
      } satisfies UIFragmentOptions<PointerEvent>,
      {
        config: {
          callback: (...args) => this.onClickPrintFile(...args),
        },
        target: 'print-button',
      } satisfies UIFragmentOptions<PointerEvent>,
      {
        target: 'sidebar',
        config: {
          attrs: {
            // default sidebar to open
            open: true,
          },
        },
      },
      {
        target: 'sidebar-thumbnail-panel',
        config: {
          // default thumbnail panel to the active item in the sidebar
          active: true,
          attrs: {
            // Set the tooltip-title
            '@tooltip': '' ,
            'tooltip-title': 'Menu',
            'tooltip-placement': 'right',
            // Set the panel title
            // look at "The sidebar-panel properties" in the docs for info on this
            // OR SidebarPanelComponentOptions Interface Reference
            title: '',
          },
        },
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-highlight',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-strikeout',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-underline',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
      {
        target: 'fv--text-selection-tooltip>fv--text-selection-tooltip-create-bookmark',
        // remove from text-selection menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
    ];
    defaultOptions.addons = [
      getAddonPath('print'),
      getAddonPath('thumbnail'),
    ];
    defaultOptions.viewerOptions.customs = {
      /* eslint-disable no-bitwise */
      // set permissions to disable thumbnail drag-and-drop, and thumbnail contextmenu
      getDocPermissions: () =>
        this.foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.printHigh |
        this.foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.print |
        this.foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.extract |
        this.foxitModule.PDFViewCtrl.PDF.constant.User_Permissions.extractAccess,
      /* eslint-enable no-bitwise */
    };
    return defaultOptions;
  }
}


// this is from FoxitPDFSDKforWeb_DeveloperGuide.pdf:
//
// const exampleTemplate = `
//   <!-- Layout template must take <webpdf> as the root component. -->
//   <webpdf>
//     <!-- Layout templates support all html tags and properties. -->
//     <div class="toolbar" style="display:flex;flex-direction:row;padding:6px">
//       <!-- The colon prefix is the name of the component module, and the component name and module name
//       must be in lowercase. -->
//       <print:print-button></print:print-button>
//       <!-- The parameters that begin with @ are used to mark directives, the content that follow with @ is the
//       name of directives. If the directive is registered in a non-root module, then the directive name should be written
//       in the @module-name:directive-name format. -->
//
//       <xbutton name="create-typewriter-button" @controller="states:CreateTypewriterController" iconclass="fv__icon-toolbar-typewriter">toolbar.create.typewriter</xbutton>
//     </div>
//     <div class="fv__ui-body">
//       <!-- Viewer is used to display the area of PDF, so there must be a viewer component in the layout template -->
//       <viewer @touch-to-scroll></viewer>
//     </div>
//     <!-- Template is a re-written html template tag. It is typically used to hold the components that do not need to
//     be displayed immediately, such as dialog boxes, right-click menus, floating boxes, and etc.. -->
//     <template name="template-container">
//       <print:print-dialog></print:print-dialog>
//     </template>
//   </webpdf>
// `;


// default for sidebar-thumbnail-panel
// <thumbnail:sidebar-thumbnail name="sidebar-thumbnail-panel"/>


// default for sidebar-thumbnail
// <sidebar-panel name="sidebar-thumbnail" title="thumbnail:title"  class="fv__sidebar-thumbnail" icon-class="fv__icon-sidebar-thumbnail">
// 	<thumbnail:thumbnail-panel name="fv-thumbnail-panel" @controller="thumbnail:ThumbSidebarPanelController">
// 		<template>
// 			<contextmenu name="fv--thumbnail-contextmenu">
// 				<contextmenu-item @controller="thumbnail:RotateLeftController" name="contextmenu-item-thumbnail-rotate-left">thumbnail:contextmenu.rotateLeft</contextmenu-item>
// 				<contextmenu-item @controller="thumbnail:RotateRightController" name="contextmenu-item-thumbnail-rotate-right">thumbnail:contextmenu.rotateRight</contextmenu-item>
// 				<contextmenu-separator></contextmenu-separator>
// 				<contextmenu-item @controller="thumbnail:InsertBlankPageController" name="contextmenu-item-thumbnail-add-blank-page">thumbnail:contextmenu.addBlankPage</contextmenu-item>
// 				<contextmenu-separator></contextmenu-separator>
// 				<contextmenu-item @controller="thumbnail:DeletePageController" name="contextmenu-item-thumbnail-delete">thumbnail:contextmenu.delete</contextmenu-item>
// 			</contextmenu>
// 		</template>
// 	</thumbnail:thumbnail-panel>
// </sidebar-panel>


// Changing doc permissions seems to be the more standard way to control the behavior of buttons or panels across the board,
// which applies to the need to disable the drag-and-drop, and contextmenu for the thumbnail-panel.
//
// But, here are some alternatives that I came up with before realizing this:
// For context menu stuff, search for => 'Hiding the right-click menu or items' in Developer Guide
// => fragments => cls: "fv__ui-force-hide", or override createContextMenu
//
// If we want to disable dragging in the Thumbnail sidebar, then 1 way would be to
// replace the controller of 'fv-thumbnail-panel' with a subclass of the ThumbSidebarPanelController, and override mounted(),
// so that after that call, we can set this.enableDragAndDrop(0)
// and then return the result of the call.
// Also, we would have to patch 'enableDragAndDrop', so that it doesn't get called, and re-enabled later, which can happen from a handler for renderFileSuccess.
// OR
// use getComponentByName('fv-thumbnail-panel') => the result would already be mounted => pass to lockToReadonly()
// OR
// Contacting Foxit support may be necessary, since some of this seems hacky

// return teardown fn
// function lockToReadonly(component: ThumbnailPanelComponent) {
//   component.enableDragAndDrop(0);
//   return patchObjectFn(component, 'enableDragAndDrop', () => {
//     return () => {
//       // noop
//     };
//   });
// }
